import { useContext, useEffect, useMemo, useState } from "react";

import {
  CHART_TYPE_DOWNLOAD_CSV,
  CHART_TYPE_TIMESERIES_COUNTY,
  ChartBase,
} from "@datanac/datanac-visualization-toolkit/dist/Components/chart/ChartBase";
import { Box, Button, Card, CardContent, Divider, Grid, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
import TilleyFormSelect from "components/Core/TilleyFormSelect";
import DropdownSelector from "components/DropdownSelector";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { useLocalStorage } from "core/useLocalStorage";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { formatDateLong, formatNumber } from "utility/formatters";

import { CloudyIcon, ReportsIcon, SunIcon } from "../Icons/Icons";
import CropTypeSelect from "./CropTypeSelect";
import { GrowthStageProgress } from "./GrowthStageProgress";
import QuickSelect from "./QuickSelect";
import { useWeatherReport } from "./useWeatherReport";
import { CHART_COLORS, DEFAULT_YEAR, WEATHER_FIELDS } from "./WeatherHelper";
import { YieldForecast } from "./YieldForecast";

export const WeatherDashboardWorkspace = () => {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const currentDate = formatDateLong(new Date());

  // Component state
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year || DEFAULT_YEAR);
  const [selectedCropType, setSelectedCropType] = useState("corn");
  const [dateRange, setDateRange] = useState({
    start: new Date(DEFAULT_YEAR, 0, 1),
    end: new Date(DEFAULT_YEAR, 11, 31),
  });

  // Location selection state with localStorage
  const [weatherLocationState, setWeatherLocationState] = useLocalStorage("weather_location_state", "");
  const [weatherLocationCounty, setWeatherLocationCounty] = useLocalStorage("weather_location_county", "");
  const [weatherLocationSubcounty, setWeatherLocationSubcounty] = useLocalStorage("weather_location_subcounty", "");

  // Create locationState object from individual localStorage values
  const locationState = useMemo(
    () => ({
      state_name: weatherLocationState,
      county_name: weatherLocationCounty,
      sub_county_code: weatherLocationSubcounty,
    }),
    [weatherLocationState, weatherLocationCounty, weatherLocationSubcounty]
  );

  // Use the custom weather report hook
  const {
    // weatherData,
    // yieldForecastData,
    isLoadingWeather,
    isLoadingYieldForecast,
    isLoadingCountyData,
    temperatureChartData,
    precipitationChartData,
    windChartData,
    droughtChartData,
    gddChartData,
    totalPrecipitation,
    cumulativeGDD,
    averageTemperature,
    currentGrowthStage,
    latestDroughtIndex,
    yieldForecastValues,
    rmaProjectedYieldIrrigated,
    rmaProjectedYieldNonIrrigated,
    rmaHistoricalYieldIrrigated,
    rmaHistoricalYieldNonIrrigated,
    isLoadingHistoricalYieldTrend,
    // actions,
  } = useWeatherReport({
    dateRange,
    locationState,
    currentYear,
    selectedCropType,
    globalState,
    enqueueSnackbar,
    doLoadPriorYear: true, // Enable prior year loading
  });

  // Handle location state changes
  const handleLocationChange = (event, value) => {
    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    // Update the appropriate localStorage value based on field name
    if (eventName === "state_name") {
      setWeatherLocationState(value);
      // Reset dependent fields when parent changes
      setWeatherLocationCounty("");
      setWeatherLocationSubcounty("");
    } else if (eventName === "county_name") {
      setWeatherLocationCounty(value);
      // Reset dependent field when parent changes
      setWeatherLocationSubcounty("");
    } else if (eventName === "sub_county_code") {
      setWeatherLocationSubcounty(value);
    }
  };

  // Update date range when year changes
  useEffect(() => {
    // Preserve the current month/day but update to the new year
    const newStartDate = new Date(dateRange.start);
    const newEndDate = new Date(dateRange.end);

    newStartDate.setFullYear(currentYear);
    newEndDate.setFullYear(currentYear);

    setDateRange({
      start: newStartDate,
      end: newEndDate,
    });
  }, [currentYear]);

  useEffect(() => {
    if (!selectedCropType) {
      setSelectedCropType("corn");
    }
  }, [selectedCropType]);
  useEffect(() => {
    if (globalState?.crop_year !== currentYear) {
      globalDispatch({ type: "update", payload: { key: "crop_year", value: currentYear } });
    }
  }, [currentYear, globalDispatch, globalState?.crop_year]);

  return (
    <div className="weather-dashboard">
      <WorkspaceHeader
        title="Yield Forecast"
        subtitle={
          locationState.state_name && locationState.county_name ? (
            `${locationState.state_name}, ${locationState.county_name}`
          ) : (
            <>&nbsp;</>
          )
        }
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }} className="only-print">
        {`as of ${currentDate}`}
      </Typography>

      {/* Location Selection */}
      <Box className="location-selection">
        <Grid container spacing={2} className="no-print">
          <Grid item xs={12} lg={2} md={3}>
            <DataFormSelect
              value={locationState.state_name}
              name="state_name"
              onChange={handleLocationChange}
              objectName="State"
              itemFilter={{ reinsurance_year: currentYear }}
              itemGetKey={(item) => item.state_name}
              itemGetLabel={(item) => item.state_name}
              doFetch={currentYear}
              isLoading={isLoadingWeather}
            />
          </Grid>
          <Grid item xs={12} lg={2} md={3}>
            <DataFormSelect
              value={locationState.county_name}
              name="county_name"
              onChange={handleLocationChange}
              objectName="County"
              itemFilter={{
                reinsurance_year: currentYear,
                state_name: locationState.state_name,
              }}
              itemGetKey={(item) => item.county_name}
              itemGetLabel={(item) => item.county_name}
              doFetch={currentYear && locationState.state_name}
              isLoading={isLoadingWeather}
            />
          </Grid>
          <Grid item xs={12} lg={2} md={3}>
            <TilleyFormSelect
              value={locationState.sub_county_code}
              name="sub_county_code"
              onChange={handleLocationChange}
              objectName="Subcounty"
              itemFilter={{
                state_name: locationState.state_name,
                county_name: locationState.county_name,
              }}
              itemGetKey={(item) => item.sub_county_code}
              itemGetLabel={(item) => item.sub_county_code}
              doFetch={locationState.state_name && locationState.county_name}
              isLoading={isLoadingWeather}
              query={async (filters) => {
                return await ApiHelper.callSmociObject(
                  "data/DATANAC",
                  "GIS",
                  "Subcounty",
                  "SelectObject",
                  filters,
                  globalState
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <CropTypeSelect
              selectedCropType={selectedCropType}
              setSelectedCropType={setSelectedCropType}
              isLoadingWeather={isLoadingWeather}
            />
          </Grid>
        </Grid>

        {/* Only show date controls if location is selected */}
        {locationState.state_name && locationState.county_name && locationState.sub_county_code && (
          <>
            <Divider className="no-print" />
            <Box className="date-controls no-print">
              <Box className="date-control-item year-selector">
                <DropdownSelector
                  value={currentYear}
                  onChange={setCurrentYear}
                  options={getBudgetYears()}
                  popupId="weather-year-menu"
                />
              </Box>

              <Box className="date-control-item start-date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    format="MM/DD/YYYY"
                    value={dateRange?.start ? dayjs(dateRange.start) : dayjs(new Date(currentYear, 0, 1))}
                    onChange={(val) =>
                      setDateRange((prev) => ({ ...prev, start: val?.toDate() || new Date(currentYear, 0, 1) }))
                    }
                    slots={{ textField: TextField }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box className="date-control-item end-date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    format="MM/DD/YYYY"
                    value={dateRange?.end ? dayjs(dateRange.end) : dayjs(new Date(currentYear, 11, 31))}
                    onChange={(val) =>
                      setDateRange((prev) => ({ ...prev, end: val?.toDate() || new Date(currentYear, 11, 31) }))
                    }
                    slots={{ textField: TextField }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box className="date-control-item quick-select">
                <QuickSelect
                  selectedCropType={selectedCropType}
                  currentYear={currentYear}
                  setDateRange={setDateRange}
                />
              </Box>

              <Box className="date-control-item reset-button">
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const startOfSelectedYear = new Date(currentYear, 0, 1);
                    const endOfSelectedYear = new Date(currentYear, 11, 31);
                    setDateRange({ start: startOfSelectedYear, end: endOfSelectedYear });
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
            <Box
              className="only-print"
              sx={{ fontSize: "0.85rem", lineHeight: "1.2", marginBottom: "0.5rem", marginTop: "0.5rem" }}
            >
              {dayjs(dateRange?.start || new Date(currentYear, 0, 1)).format("MM/DD/YYYY")} -{" "}
              {dayjs(dateRange?.end || new Date(currentYear, 11, 31)).format("MM/DD/YYYY")}
            </Box>
          </>
        )}
      </Box>
      <Divider className="no-print" />
      {/* Dashboard Content - Only show if location is selected */}
      {locationState.state_name && locationState.county_name && locationState.sub_county_code ? (
        <Box className="dashboard-content">
          <Grid container spacing={2}>
            <Grid item md={12} lg={6} className="dashboard-card" sx={{ width: "100%" }}>
              <Card>
                <CardContent sx={{ width: "100%", p: 2 }}>
                  <Box className="card-header">
                    <SunIcon />
                    <Typography>Temperature</Typography>
                  </Box>
                  <Box className="section-container">
                    <Typography className="section-title">Average Temperatures</Typography>
                    <Box className="stats-container">
                      <Box className="stats-item">
                        <Typography color={"error.main"} className="stats-value">
                          {formatNumber(averageTemperature.max, 1)}°F
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Avg High
                        </Typography>
                      </Box>
                      <Box className="stats-item">
                        <Typography color={"info.main"} className="stats-value">
                          {formatNumber(averageTemperature.min, 1)}°F
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Avg Low
                        </Typography>
                      </Box>
                      <Box className="stats-item">
                        <Typography color={"text.primary"} className="stats-value">
                          {formatNumber(cumulativeGDD, 0)}
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Cumulative GDD
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <YieldForecast
                      selectedCropType={selectedCropType}
                      isLoadingYieldForecast={isLoadingYieldForecast}
                      isLoadingCountyData={isLoadingCountyData}
                      isLoadingHistoricalYieldTrend={isLoadingHistoricalYieldTrend}
                      yieldForecastValues={yieldForecastValues}
                      rmaProjectedYieldIrrigated={rmaProjectedYieldIrrigated}
                      rmaProjectedYieldNonIrrigated={rmaProjectedYieldNonIrrigated}
                      rmaHistoricalYieldIrrigated={rmaHistoricalYieldIrrigated}
                      rmaHistoricalYieldNonIrrigated={rmaHistoricalYieldNonIrrigated}
                    />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Growth Stage Progress - {selectedCropType}</Typography>
                    <GrowthStageProgress cropType={selectedCropType} currentStageData={currentGrowthStage} />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Temperature</Typography>
                    <ChartBase
                      key={`temperature-${currentYear}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={temperatureChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY={WEATHER_FIELDS.MAX_TEMP}
                      fieldNameY2={WEATHER_FIELDS.MIN_TEMP}
                      fieldNameY3={`${WEATHER_FIELDS.MAX_TEMP_PRIOR}`}
                      title=""
                      filterValue=""
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      renderer="svg"
                      scaleColors={[CHART_COLORS.TEMPERATURE.MAX, CHART_COLORS.TEMPERATURE.MIN, CHART_COLORS.PRIOR]}
                    />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Growing Degree Days - {selectedCropType}</Typography>
                    <ChartBase
                      key={`gdd-${currentYear}-${selectedCropType}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={gddChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY="cumulative_gdd"
                      fieldNameY2="cumulative_gdd_prior"
                      title=""
                      filterValue=""
                      renderer="svg"
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      scaleColors={[CHART_COLORS.GDD, CHART_COLORS.PRIOR]}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item md={12} lg={6} className="dashboard-card" sx={{ width: "100%" }}>
              <Card>
                <CardContent sx={{ width: "100%", p: 2 }}>
                  <Box className="card-header">
                    <CloudyIcon />
                    <Typography>Precipitation</Typography>
                  </Box>
                  <Box className="precipitation-section">
                    <Typography className="section-title">Drought Severity & Precipitation</Typography>
                    <Box className="stats-container">
                      <Box className="stats-item">
                        <Typography color={"text.primary"} className="stats-value">
                          {latestDroughtIndex?.severity || "N/A"}
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Drought Status
                        </Typography>
                      </Box>
                      <Box className="stats-item centered">
                        <Typography sx={{ color: CHART_COLORS.DROUGHT.PDSI }} className="stats-value">
                          {formatNumber(latestDroughtIndex?.pdsi || 0, 2)}
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Long Term PDSI
                        </Typography>
                      </Box>
                      <Box className="stats-item centered">
                        <Typography
                          sx={{
                            color: CHART_COLORS.DROUGHT.Z_INDEX,
                          }}
                          className="stats-value"
                        >
                          {formatNumber(latestDroughtIndex?.z_index || 0, 2)}
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Drought Z-Index
                        </Typography>
                      </Box>
                      <Box className="stats-item">
                        <Typography color={"text.primary"} className="stats-value">
                          {formatNumber(totalPrecipitation, 2) + " in"}
                        </Typography>
                        <Typography variant="subtitle2" className="stats-label">
                          Total Precipitation
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Weekly Precipitation (in)</Typography>
                    <ChartBase
                      key={`precipitation-${currentYear}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={precipitationChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY={WEATHER_FIELDS.PRECIPITATION}
                      fieldNameY2={`${WEATHER_FIELDS.PRECIPITATION_PRIOR}`}
                      title=""
                      filterValue=""
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      renderer="svg"
                      scaleColors={[CHART_COLORS.PRECIPITATION, CHART_COLORS.PRIOR]}
                    />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Short-Term Drought (Z-Index)</Typography>
                    <ChartBase
                      key={`drought-${currentYear}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={droughtChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY={WEATHER_FIELDS.Z_INDEX}
                      fieldNameY2={WEATHER_FIELDS.Z_INDEX_PRIOR}
                      title=""
                      filterValue=""
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      renderer="svg"
                      scaleColors={[CHART_COLORS.DROUGHT.Z_INDEX, CHART_COLORS.PRIOR]}
                    />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Long-Term Drought (PDSI)</Typography>
                    <ChartBase
                      key={`drought2-${currentYear}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={droughtChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY={WEATHER_FIELDS.PDSI}
                      fieldNameY2={WEATHER_FIELDS.PDSI_PRIOR}
                      title=""
                      filterValue=""
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      renderer="svg"
                      scaleColors={[CHART_COLORS.DROUGHT.PDSI, CHART_COLORS.PRIOR]}
                    />
                  </Box>
                  <Divider />
                  <Box className="section-container">
                    <Typography className="section-title">Wind Speed</Typography>
                    <ChartBase
                      key={`wind-${currentYear}-${locationState.sub_county_code}-${dateRange.start?.getTime()}-${dateRange.end?.getTime()}`}
                      data={windChartData}
                      fieldNameX={WEATHER_FIELDS.DATE}
                      fieldNameY={WEATHER_FIELDS.WIND_SPEED}
                      title=""
                      filterValue=""
                      allowedChartTypes={[CHART_TYPE_TIMESERIES_COUNTY, CHART_TYPE_DOWNLOAD_CSV]}
                      size="small"
                      renderer="svg"
                      scaleColors={[CHART_COLORS.WIND]}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box className="empty-state">
          <Typography variant="h6" color="text.secondary">
            Please select a state, county, and subcounty to view weather data.
          </Typography>
        </Box>
      )}
    </div>
  );
};
